/**
 * Narrow "fail-open" helper for secondary data loads.
 *
 * `.catch(() => [])` swallows EVERY failure, so a 500 / 401 / 403 masquerades
 * as "no data". Use `.catch(onAbsent(fallback))` instead: it degrades to the
 * fallback ONLY when the feature is genuinely absent / not rolled out
 * (404 / 501 / 503 — same set as lib/api/compliance.ts) and rethrows anything
 * else so the caller's error state can surface the real problem.
 */

import { ApiError } from '@/lib/api/client';

/** HTTP statuses meaning "feature unavailable" -> safe to degrade to empty. */
const ABSENT_STATUSES = [404, 501, 503];

export function isAbsent(error: unknown): boolean {
  return error instanceof ApiError && ABSENT_STATUSES.includes(error.status);
}

/** Promise `.catch` handler: return `fallback` only when the feature is absent, else rethrow. */
export function onAbsent<T>(fallback: T): (error: unknown) => T {
  return (error: unknown) => {
    if (isAbsent(error)) return fallback;
    throw error;
  };
}

/**
 * The availability marker, if any, lives either at the body top level or nested
 * under `detail` (some shared/community-style 501/503s mirror it there). Returns
 * whichever object holds it, else null. Centralized from the per-file copies in
 * ads.ts / listening.ts / web-listening.ts.
 */
function unavailabilityPayload(body: unknown): Record<string, unknown> | null {
  if (!body || typeof body !== 'object') return null;
  const top = body as Record<string, unknown>;
  const detail = top.detail;
  if (detail && typeof detail === 'object') return detail as Record<string, unknown>;
  return top;
}

/**
 * True only when the error body carries an EXPLICIT availability discriminator —
 * `implemented`/`configured`/`external_api_configured`/`ai_available`/
 * `connector_configured` === false, or a `feature_flag` string — never inferred
 * from the HTTP status alone.
 *
 * This is the by-id/detail-loader guard: a BARE 404 (a deleted/unknown resource)
 * or an empty 503 with no such marker is a REAL error — a genuine not-found or a
 * transient failure — not "feature not deployed", so it must surface rather than
 * render a false "service en cours de déploiement / pas encore disponible"
 * banner. Combine with a status check (404/501/503) at the call site, exactly as
 * `ads.ts#isFeatureUnavailable` does. LIST / singleton GETs intentionally stay
 * bare-status (a missing collection route legitimately means "not rolled out").
 */
export function hasUnavailableDiscriminator(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  const payload = unavailabilityPayload(err.body);
  if (!payload) return false;
  return (
    payload.implemented === false ||
    payload.external_api_configured === false ||
    payload.ai_available === false ||
    payload.configured === false ||
    payload.connector_configured === false ||
    typeof payload.feature_flag === 'string'
  );
}
